import { useCallback } from "react";
import { Modal } from "antd";
import { useTranslation } from "react-i18next";
import apiClient from "../../../../helpers/apiClient";
import { useToast } from "../../../../contexts/ToastContext";
import { isEmployeeActive } from "./employeeHelpers";

/**
 * Status change and delete for an employee, with confirmations. Shared by the list and the
 * details page so the wording ("Activate/Deactivate Employee") and behaviour stay identical.
 */
export default function useEmployeeActions() {
    const { t } = useTranslation("settings");
    const toast = useToast();

    const getStatusActionLabel = useCallback(
        (employee) =>
            isEmployeeActive(employee) ? t("employees.deactivateEmployee") : t("employees.activateEmployee"),
        [t],
    );

    const changeStatus = useCallback(
        (employee, onDone) => {
            const activating = !isEmployeeActive(employee);
            Modal.confirm({
                title: activating ? t("employees.activateEmployeeTitle") : t("employees.deactivateEmployeeTitle"),
                content: activating
                    ? t("employees.activateEmployeeDescription")
                    : t("employees.deactivateEmployeeDescription"),
                okText: activating ? t("employees.activateEmployee") : t("employees.deactivateEmployee"),
                okButtonProps: activating ? undefined : { danger: true },
                cancelText: t("cancel"),
                centered: true,
                onOk: async () => {
                    try {
                        const res = await apiClient.patch(`/api/employee/${employee.id}`, {
                            status: activating ? "active" : "inactive",
                        });
                        if (!res?.data?.success) throw new Error(res?.data?.message);
                        toast.success({ message: t("employees.statusUpdated") });
                        onDone?.();
                    } catch (error) {
                        toast.error({
                            message: t("employees.errorUpdatingEmployeeStatus"),
                            description: error?.response?.data?.message || error?.message,
                        });
                    }
                },
            });
        },
        [t, toast],
    );

    const deleteEmployee = useCallback(
        (employee, onDone) => {
            Modal.confirm({
                title: t("employees.confirmDeleteEmployee"),
                okText: t("employees.deleteEmployee"),
                okButtonProps: { danger: true },
                cancelText: t("cancel"),
                centered: true,
                onOk: async () => {
                    try {
                        const res = await apiClient.delete(`/api/employee/${employee.id}`);
                        if (!res?.data?.success) throw new Error(res?.data?.message);
                        toast.success({ message: t("employees.employeeDeletedSuccess") });
                        onDone?.();
                    } catch (error) {
                        toast.error({ message: t("employees.couldNotDelete") });
                    }
                },
            });
        },
        [t, toast],
    );

    return { changeStatus, deleteEmployee, getStatusActionLabel };
}
